'use client';

import type { CSSProperties } from 'react';
import { Link } from '@/components/ui';
import Carousel from '@/components/ui/carousel/carousel';
import { useResolveThemeColor } from '@/hooks/use-resolve-theme-color';
import { cn } from '@/utils';
import { GUMLET_PRESETS } from '@/utils/gumlet';
import { selectMainImage } from '@/utils/select-main-image';
import type {
  ImageGridItem,
  ImageGridSectionProps,
} from '../image-grid-section/image-grid-section';

export type ImageGridSectionPropsWithArrowColor = ImageGridSectionProps & {
  arrowColor?: string;
  paddingStyle?: CSSProperties;
};

const CategoryCarouselSection = ({
  height,
  background,
  title,
  items,
  arrowColor,
  paddingStyle,
}: ImageGridSectionPropsWithArrowColor) => {
  const resolveThemeColor = useResolveThemeColor();
  const resolvedArrowColor = resolveThemeColor(arrowColor || title.color);

  return (
    <section
      className="flex w-full flex-col items-center justify-center gap-8"
      style={{ minHeight: height, backgroundColor: resolveThemeColor(background), ...paddingStyle }}
    >
      {title.text && (
        <h2
          className={cn('text-center font-semibold', {
            'font-normal': title.fontWeight === 'light',
            'font-medium': title.fontWeight === 'normal',
            'font-bold': title.fontWeight === 'bold' || title.fontWeight === 'semi-bold',
          })}
          style={{ color: resolveThemeColor(title.color), fontSize: title.fontSize }}
        >
          {title.text}
        </h2>
      )}
      <div className="w-full max-w-6xl">
        <Carousel
          slides={items}
          options={{
            align: 'center',
            slidesToScroll: 1,
            breakpoints: {
              '(min-width: 768px)': {
                slidesToScroll: 3,
              },
            },
          }}
          arrowStyle={{ color: resolvedArrowColor, borderColor: resolvedArrowColor }}
        >
          {(item: ImageGridItem) => {
            const imageSrc = selectMainImage([item.src], GUMLET_PRESETS.gridTile);
            return (
              <div
                key={item.label}
                className="min-w-0 flex-[0_0_50%] sm:flex-[0_0_25%] lg:flex-[0_0_20%]"
              >
                <Link
                  noStyle
                  href={item.href}
                  className="group relative block aspect-[4/3] overflow-hidden rounded-lg"
                >
                  <span
                    className="absolute inset-0 bg-cover bg-center bg-no-repeat transition-transform duration-300 group-hover:scale-105"
                    style={{ backgroundImage: `url(${imageSrc})` }}
                  />
                  <span className="absolute inset-0 bg-black/50" aria-hidden />
                  <span className="absolute inset-0 flex items-center justify-center p-3 text-center text-sm font-medium tracking-wide whitespace-pre-line text-white uppercase">
                    {item.label}
                  </span>
                </Link>
              </div>
            );
          }}
        </Carousel>
      </div>
    </section>
  );
};

export default CategoryCarouselSection;
